import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API_ENDPOINTS } from "../config/api";
import {
  FaPlus,
  FaSave,
  FaTrash,
  FaCloudUploadAlt,
  FaCheckCircle,
  FaSearch,
  FaOm,
  FaClock,
  FaPray,
} from "react-icons/fa";
import "../styles/ManagePoojas.css";

const API = API_ENDPOINTS.POOJAS;
const UPLOAD_API = API_ENDPOINTS.POOJAS_UPLOAD;

const ManagePoojas = () => {
  const [poojas, setPoojas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Redesign Search Query
  const [searchQuery, setSearchQuery] = useState("");

  /* ================= FETCH POOJAS ================= */
  const fetchPoojas = async () => {
    try {
      setLoading(true);
      const res = await fetch(API);
      const data = await res.json();
      setPoojas(data || []);
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPoojas();
  }, []);

  /* ================= UPDATE FIELD ================= */
  const updatePooja = (i, field, value) => {
    setPoojas((prev) => {
      const updated = [...prev];
      updated[i] = {
        ...updated[i],
        [field]: value,
      };
      return updated;
    });
  };

  /* ================= ADD POOJA ================= */
  const addPooja = () => {
    setPoojas((prev) => [
      {
        name: "",
        time: "",
        desc: "",
        icon: "🪔",
        images: [],
      },
      ...prev,
    ]);
  };

  /* ================= UPLOAD IMAGES ================= */
  const handleImageUpload = async (i, files) => {
    try {
      const formData = new FormData();

      Array.from(files).forEach((file) => {
        formData.append("images", file);
      });

      const res = await fetch(UPLOAD_API, {
        method: "POST",
        body: formData,
      });

      const urls = await res.json();

      setPoojas((prev) => {
        const updated = [...prev];
        updated[i] = {
          ...updated[i],
          images: [...(updated[i]?.images || []), ...urls],
        };
        return updated;
      });
    } catch (err) {
      console.error("Upload error:", err);
      alert("Image upload failed");
    }
  };

  /* ================= REMOVE IMAGE ================= */
  const removeImage = (poojaIndex, imgIndex) => {
    setPoojas((prev) => {
      const updated = [...prev];
      const images = [...(updated[poojaIndex]?.images || [])];
      images.splice(imgIndex, 1);

      updated[poojaIndex] = {
        ...updated[poojaIndex],
        images,
      };
      return updated;
    });
  };

  /* ================= DELETE POOJA ================= */
  const deletePooja = async (i, id) => {
    if (window.confirm("Are you sure you want to delete this Pooja?")) {
      try {
        if (id) {
          await fetch(`${API}/${id}`, {
            method: "DELETE",
          });
        }
        setPoojas((prev) => prev.filter((_, index) => index !== i));
      } catch (err) {
        console.error("Delete error:", err);
      }
    }
  };

  /* ================= SAVE POOJAS ================= */
  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveSuccess(false);

      const cleaned = poojas.map((p) => ({
        _id: p._id || undefined,
        name: p.name,
        icon: p.icon || "🪔",
        time: p.time || "",
        desc: p.desc || "",
        images: Array.isArray(p.images) ? p.images : [],
      }));

      const res = await fetch(API, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cleaned),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || "Save failed");
      }

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      alert("🎉 Poojas Saved Successfully!");
      fetchPoojas();
    } catch (err) {
      console.error("Save error:", err);
      alert(err.message || "❌ Failed to save poojas");
    } finally {
      setSaving(false);
    }
  };

  /* ================= RENDER LOADING ================= */
  if (loading) {
    return (
      <div className="poojas-loading">
        <div className="spinner"></div>
        <h2>Loading Poojas Directory...</h2>
        <p>Connecting to backend API</p>
      </div>
    );
  }

  /* ================= FILTER LOGIC ================= */
  const filteredPoojas = poojas.filter(p => 
    p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.desc?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="manage-poojas-container">
      {/* BANNER SECTION */}
      <div className="poojas-banner">
        <div className="banner-content">
          <h1>🛕 Manage Temple Poojas</h1>
          <p>Add, edit, or configure details, timings, emoji symbols, and photo galleries for all the various worship rituals.</p>
        </div>
        <div className="banner-stats">
          <div className="stat-pill">
            <FaOm className="icon" />
            <span><strong>{poojas.length}</strong> Registered Poojas</span>
          </div>
        </div>
      </div>

      {/* DASHBOARD CONTROLS & FILTER */}
      <div className="dashboard-controls">
        <div className="search-filter-box">
          <div className="search-input-wrapper">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search poojas by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="quick-action-buttons">
          <Link
            to="/admin/pooja-bookings"
            className="action-btn"
            style={{
              background: "#0f2d5c",
              color: "#f5c542",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: "7px",
              fontWeight: "700",
              border: "1px solid #f5c542",
              boxShadow: "0 4px 12px rgba(15, 45, 92, 0.2)",
            }}
          >
            <FaPray /> View Devotee Bookings
          </Link>

          <button className="action-btn add-btn-primary" onClick={addPooja}>
            <FaPlus /> Add Pooja
          </button>
          
          <button
            className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="spinner-small"></div> Saving...
              </>
            ) : saveSuccess ? (
              <>
                <FaCheckCircle /> Saved!
              </>
            ) : (
              <>
                <FaSave /> Save Changes
              </>
            )}
          </button>
        </div>
      </div>

      {/* PANEL/GRID CONTAINER */}
      <div className="panel-container">
        {filteredPoojas.length === 0 ? (
          <div className="empty-state">
            <FaOm className="empty-icon" />
            <h3>No Poojas Found</h3>
            <p>{searchQuery ? "Try searching for a different keyword." : "Add a new pooja session to display on the website."}</p>
            <button className="action-btn add-btn-primary" onClick={addPooja}>
              <FaPlus /> Add Pooja Now
            </button>
          </div>
        ) : (
          <div className="pooja-layout-grid">
            {filteredPoojas.map((item, i) => (
              <div className="pooja-entry-card" key={item._id || i}>
                <div className="pooja-card-top-header">
                  <div className="pooja-avatar-badge">
                    <span className="avatar-emoji">{item.icon || "🪔"}</span>
                  </div>
                  <button className="pooja-delete-btn" onClick={() => deletePooja(i, item._id)}>
                    <FaTrash /> Delete
                  </button>
                </div>

                <div className="pooja-card-form">
                  <div className="form-row-split">
                    <div className="form-group-compact flex-small">
                      <label>Symbol / Emoji</label>
                      <input
                        type="text"
                        value={item.icon || ""}
                        onChange={(e) => updatePooja(i, "icon", e.target.value)}
                        placeholder="e.g. 🌸"
                        maxLength={5}
                      />
                    </div>
                    
                    <div className="form-group-compact flex-grow">
                      <label>Pooja Name / Title</label>
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updatePooja(i, "name", e.target.value)}
                        placeholder="e.g. Abhishekam"
                      />
                    </div>
                  </div>

                  <div className="form-group-compact">
                    <label><FaClock className="label-icon" /> Timing / Frequency</label>
                    <input
                      type="text"
                      value={item.time}
                      onChange={(e) => updatePooja(i, "time", e.target.value)}
                      placeholder="e.g. Daily morning at 6:30 AM"
                    />
                  </div>

                  <div className="form-group-compact">
                    <label>Description / Significance</label>
                    <textarea
                      value={item.desc}
                      onChange={(e) => updatePooja(i, "desc", e.target.value)}
                      placeholder="Explain the pooja procedures, prasadam distribution..."
                      rows={4}
                    />
                  </div>

                  <div className="form-group-compact">
                    <label>Pooja Photos Gallery</label>
                    <label className="custom-file-upload">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={(e) => handleImageUpload(i, e.target.files)}
                      />
                      <FaCloudUploadAlt className="icon" />
                      <span>Upload Photos</span>
                    </label>
                  </div>

                  {/* IMAGES PREVIEW */}
                  {item.images && item.images.length > 0 && (
                    <div className="pooja-gallery-preview">
                      {item.images.map((img, imgIndex) => (
                        <div key={imgIndex} className="pooja-preview-box">
                          <img src={img} alt="pooja preview" className="pooja-thumb-img" />
                          <button
                            className="pooja-thumb-delete-btn"
                            onClick={() => removeImage(i, imgIndex)}
                            title="Remove image"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* FLOATING SAVE BAR */}
      <div className="floating-save-bar">
        <div className="save-bar-info">
          <span>Unsaved changes to your poojas list will be lost.</span>
        </div>
        <button
          className={`action-btn save-btn-primary ${saving ? "saving" : ""} ${saveSuccess ? "saved" : ""}`}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? (
            <>
              <div className="spinner-small"></div> Saving...
            </>
          ) : saveSuccess ? (
            <>
              <FaCheckCircle /> Saved Successfully!
            </>
          ) : (
            <>
              <FaSave /> Save Changes
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ManagePoojas;
